// Mapbox canvas with refined light styling. A single GeoJSON source feeds:
//   1. cluster bubbles (low zooms)
//   2. shadow drop      (subtle elevation under each marker)
//   3. base point       (every outbreak)
//   4. selected ring    (indigo ring around selected outbreak)
//   5. signal pulse     (active outbreaks, animated)
//
// All rendering is WebGL — no DOM markers, no per-frame React reconciles.
import { useEffect, useRef } from 'react';
import mapboxgl, { type GeoJSONSource, type Map as MapboxMap } from 'mapbox-gl';
import type { OutbreakWithSources, Pathogen } from '../types/supabase';
import { PATHOGEN_COLOR, markerRadius } from '../lib/palette';
import { totalCases } from '../lib/outbreaks';

interface Props {
  outbreaks: OutbreakWithSources[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** 'markers' shows the discrete circles; 'heatmap' shows a density
   *  gradient weighted by case count; 'choropleth' fills country
   *  polygons by total case count. */
  viewMode?: 'markers' | 'heatmap' | 'choropleth';
  /** Initial camera position. Driven by URL params lat/lng/zoom on landing
   *  pages (e.g. /where/dubai → opens map centered on Dubai). Falls back to
   *  the global default when not supplied. */
  initialCamera?: { lat: number; lng: number; zoom: number };
}

// Two basemap styles. The choropleth view swaps to the full streets
// style — coloured terrain, water, road network — so the country fills
// sit on top of recognisable cartography. Markers + heatmap stay on the
// cosmic satellite globe. Switching styles re-fires `style.load`, which
// re-runs installLayers + pushData + applyViewMode below.
const STYLE_STREETS = 'mapbox://styles/mapbox/streets-v12';

function styleForMode(_mode: 'markers' | 'heatmap' | 'choropleth'): string {
  // The flat light "By country" basemap is now the default across every
  // view mode; the satellite globe is retired from the rotation.
  return STYLE_STREETS;
}

const SOURCE = 'outbreaks';
// The clustered source above only emits cluster features at low zoom,
// which makes the heatmap layer (filtered to non-cluster points) invisible
// when zoomed out. A second non-clustered source backs the heatmap so
// every event contributes its weight at every zoom.
const SOURCE_FLAT = 'outbreaks-flat';
const SOURCE_COUNTRIES = 'mapbox-country-boundaries';
const LAYER_HEATMAP = 'outbreaks/heatmap';
const LAYER_CHOROPLETH = 'outbreaks/choropleth';
const LAYER_CHOROPLETH_OUTLINE = 'outbreaks/choropleth-outline';
const LAYER_CLUSTER_GLOW = 'outbreaks/cluster-glow';
const LAYER_CLUSTER_BG = 'outbreaks/cluster-bg';
const LAYER_CLUSTER_RING = 'outbreaks/cluster-ring';
const LAYER_CLUSTER_COUNT = 'outbreaks/cluster-count';
const LAYER_PULSE = 'outbreaks/pulse';
const LAYER_SHADOW = 'outbreaks/shadow';
const LAYER_SELECTED = 'outbreaks/selected';
const LAYER_BASE = 'outbreaks/base';

const MARKER_LAYERS = [
  LAYER_CLUSTER_GLOW,
  LAYER_CLUSTER_BG,
  LAYER_CLUSTER_RING,
  LAYER_CLUSTER_COUNT,
  LAYER_PULSE,
  LAYER_SHADOW,
  LAYER_SELECTED,
  LAYER_BASE,
] as const;

const CHOROPLETH_LAYERS = [LAYER_CHOROPLETH, LAYER_CHOROPLETH_OUTLINE] as const;

/**
 * Aggregate per-country total case counts (confirmed + suspected) across
 * the full `countries_affected` array, not just `country_iso2`. This is
 * the right denominator for a choropleth — an outbreak that lists 10
 * countries (e.g. MV Hondius) should paint all 10, not just the one its
 * marker sits in.
 */
function casesByCountry(rows: OutbreakWithSources[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const o of rows) {
    const total = totalCases(o);
    if (total <= 0) continue;
    for (const code of o.countries_affected) {
      if (!code || code === 'INTL') continue;
      out.set(code.toUpperCase(), (out.get(code.toUpperCase()) ?? 0) + total);
    }
  }
  return out;
}

/**
 * Build a Mapbox `match` expression that maps each polygon's
 * iso_3166_1_alpha_3 / iso_3166_1 to a colour bucket. Countries with
 * zero (or missing) cases get a near-transparent grey so the underlying
 * basemap reads through. The buckets are log-scaled so a single 3,000-
 * case season doesn't flatten the rest of the data into the same red.
 */
function buildChoroplethColorExpression(byCountry: Map<string, number>): mapboxgl.ExpressionSpecification {
  const cases: Array<[string, string]> = [];
  for (const [code, count] of byCountry.entries()) cases.push([code, choroplethColor(count)]);
  if (cases.length === 0) {
    return ['literal', '#9ca3af55'] as unknown as mapboxgl.ExpressionSpecification;
  }
  // ['match', ['get', 'iso_3166_1'], 'AR', '#ef4444', 'CL', '#fb923c', …, /* default */ '#9ca3af55']
  const expr: unknown[] = ['match', ['get', 'iso_3166_1']];
  for (const [code, color] of cases) {
    expr.push(code, color);
  }
  expr.push('rgba(156, 163, 175, 0.0)'); // transparent default — basemap shows through
  return expr as unknown as mapboxgl.ExpressionSpecification;
}

function choroplethColor(cases: number): string {
  // Five-step ramp, log-ish bucketing. Opacity baked into the colour so
  // a single fill-opacity stop applies uniformly per layer.
  if (cases <= 0)     return 'rgba(156, 163, 175, 0.18)';   // grey  — coverage gap
  if (cases <= 10)    return 'rgba(252, 211, 77, 0.35)';    // soft yellow — low signal
  if (cases <= 100)   return 'rgba(251, 146, 60, 0.42)';    // orange
  if (cases <= 1000)  return 'rgba(239, 68, 68, 0.5)';      // red
  return 'rgba(153, 27, 27, 0.58)';                          // deep red — major epidemic
}

// Legend for the choropleth view. MUST stay in sync with the buckets in
// choroplethColor() above — same colours, in ascending order.
export const CHOROPLETH_LEGEND: ReadonlyArray<{ label: string; color: string }> = [
  { label: 'No reported cases', color: 'rgba(156, 163, 175, 0.18)' },
  { label: '1–10 cases',        color: 'rgba(252, 211, 77, 0.35)' },
  { label: '11–100 cases',      color: 'rgba(251, 146, 60, 0.42)' },
  { label: '101–1,000 cases',   color: 'rgba(239, 68, 68, 0.5)' },
  { label: '1,000+ cases',      color: 'rgba(153, 27, 27, 0.58)' },
];

function toFeatureCollection(rows: OutbreakWithSources[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: rows.map(o => {
      const total = totalCases(o);
      return {
        type: 'Feature',
        id: o.id,
        properties: {
          id: o.id,
          pathogen: o.pathogen,
          status: o.status,
          confidence: o.confidence,
          radius: markerRadius(total),
          // log scale for heatmap weight so a single 3000-case epidemic
          // doesn't drown out 30 small outbreaks
          weight: Math.log10(Math.max(total, 1) + 1),
          color: PATHOGEN_COLOR[o.pathogen as Pathogen] ?? '#a8a29e',
          active: o.status === 'active' ? 1 : 0,
        },
        geometry: { type: 'Point', coordinates: [o.location_lng, o.location_lat] },
      };
    }),
  };
}

export function OutbreakMap({ outbreaks, selectedId, onSelect, viewMode = 'markers', initialCamera }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const styleLoadedRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  // Mirror outbreaks into a ref so the (mount-time) style.load handler
  // can re-push the *current* dataset after setStyle wipes the layers,
  // not the empty array that was in scope when the map mounted.
  const outbreaksRef = useRef(outbreaks);
  outbreaksRef.current = outbreaks;
  // Track the style URL we last asked for so we don't redundantly call
  // setStyle (which re-loads the entire basemap).
  const currentStyleRef = useRef<string>(STYLE_STREETS);
  const pulseTRef = useRef(0);
  // Guards the one-shot intro zoom-out so a later setStyle (which re-fires
  // style.load) can't replay it.
  const introRanRef = useRef(false);

  useEffect(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (!token || !containerRef.current) return;
    mapboxgl.accessToken = token;

    // Initial style picks up the URL-driven viewMode so deep-linking with
    // `?v=choropleth` lands users on the light basemap immediately,
    // skipping the satellite-then-flash transition.
    const initialStyle = styleForMode(viewMode);
    currentStyleRef.current = initialStyle;
    // Intro: open slightly zoomed in, then ease back out to the resting
    // zoom once the map is ready — a gentle "settle into place" reveal.
    const restZoom = initialCamera ? initialCamera.zoom : 1.7;
    const INTRO_ZOOM_DELTA = 1.4;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: initialStyle,
      center: initialCamera ? [initialCamera.lng, initialCamera.lat] : [-15, 25],
      zoom: restZoom + INTRO_ZOOM_DELTA,
      minZoom: 1.2,
      maxZoom: 19,
      attributionControl: false,
      logoPosition: 'bottom-left',
      projection: { name: 'globe' },
      pitchWithRotate: false,
      dragRotate: false,
    });
    map.scrollZoom.setWheelZoomRate(1 / 220);
    map.touchZoomRotate.disableRotation();
    // Zoom controls move to bottom-left (next to Mapbox logo) to free the
    // bottom-right corner for the "Maps by Micky" credit + attribution.
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false, showZoom: true }),
      'bottom-left',
    );
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    mapRef.current = map;

    // One-shot intro: ease from the slightly-zoomed-in opening pose out
    // to the resting zoom. easeOutCubic — quick to start, gently
    // decelerating into place. Mapbox auto-shortens this to an instant
    // jump under prefers-reduced-motion.
    map.once('load', () => {
      if (introRanRef.current) return;
      introRanRef.current = true;
      map.easeTo({
        zoom: restZoom,
        duration: 1300,
        easing: (t) => 1 - Math.pow(1 - t, 3),
      });
    });

    // style.load fires on first load AND every subsequent setStyle. Each
    // time, every source/layer the previous style had is gone — so we
    // re-install everything from scratch and push the CURRENT dataset
    // (via outbreaksRef, not the closure from mount time).
    map.on('style.load', () => {
      styleLoadedRef.current = true;
      restyleBaseMap(map);
      installLayers(map);
      pushData(map, outbreaksRef.current);
      wireInteractions(map, onSelectRef);
      // Re-apply the selected outbreak's filter on the selected-ring
      // layer so a setStyle while a card is open keeps the highlight.
      const sel = (mapRef.current as MapboxMap | null)?.getStyle();
      if (sel) {
        try {
          map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], '__none__']);
        } catch { /* layer not present yet */ }
      }
      applyViewMode(map, viewModeRef.current);
    });

    // Active-outbreak pulse — modulates the radius/opacity in place.
    const pulseHandle = setInterval(() => {
      if (!mapRef.current || !styleLoadedRef.current) return;
      pulseTRef.current = (pulseTRef.current + 1) % 70;
      const phase = pulseTRef.current / 70;
      const k = 1.4 + phase * 2.4;
      // Fade in fast over the first ~12% of the cycle, then decay to 0.
      // Without the lead-in the ring was born at full opacity, so the
      // phase 69→0 wrap snapped it from ~invisible back to 0.42 every
      // ~3.5s — the "flash"/dark-pop the eye caught. Both ends now sit
      // at ~0, so the loop seam is invisible.
      const op = 0.42 * Math.min(1, phase * 8) * (1 - phase);
      try {
        mapRef.current.setPaintProperty(LAYER_PULSE, 'circle-radius', ['*', ['get', 'radius'], k]);
        mapRef.current.setPaintProperty(LAYER_PULSE, 'circle-opacity', op);
      } catch { /* style not yet loaded */ }
    }, 50);

    return () => {
      clearInterval(pulseHandle);
      map.remove();
      mapRef.current = null;
      styleLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleLoadedRef.current) return;
    pushData(map, outbreaks);
  }, [outbreaks]);

  // ----- view-mode toggle: markers vs heatmap vs choropleth ------------
  // Choropleth swaps to a flat light basemap so the country fills read
  // clearly; the other two stay on the satellite globe. setStyle wipes
  // every source/layer Mapbox knows about, so we let the existing
  // `style.load` handler re-install + re-push data + re-apply the view
  // mode. For same-style transitions we just toggle layer visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const desiredStyle = styleForMode(viewMode);
    if (desiredStyle !== currentStyleRef.current) {
      currentStyleRef.current = desiredStyle;
      styleLoadedRef.current = false;
      map.setStyle(desiredStyle);
      return;
    }
    if (!styleLoadedRef.current) return;
    applyViewMode(map, viewMode);
  }, [viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleLoadedRef.current) return;
    map.setFilter(LAYER_SELECTED, ['==', ['get', 'id'], selectedId ?? '__none__']);

    if (selectedId) {
      const o = outbreaks.find(r => r.id === selectedId);
      if (o) {
        map.flyTo({
          center: [o.location_lng, o.location_lat],
          zoom: Math.max(map.getZoom(), 3.6),
          speed: 0.9,
          curve: 1.4,
          essential: true,
        });
      }
    }
  }, [selectedId, outbreaks]);

  return (
    <div ref={containerRef} className="absolute inset-0 h-full w-full" aria-label="map of hantavirus outbreaks" />
  );
}

// =============================================================================
// helpers
// =============================================================================

/** Tune the light streets basemap: subdue road/admin overlays, render
 *  place labels as dark ink with a crisp white halo for legibility. */
function restyleBaseMap(map: MapboxMap) {
  const tweaks: Array<[string, string, unknown]> = [
    // softening the dense overlays — keep major arterials, hide minor roads
    ['road-minor', 'line-opacity', 0],
    ['road-street', 'line-opacity', 0],
    ['road-secondary-tertiary', 'line-opacity', 0.25],
    ['road-primary', 'line-opacity', 0.4],
    ['road-motorway-trunk', 'line-opacity', 0.55],
    ['road-rail', 'line-opacity', 0],
    ['tunnel-minor', 'line-opacity', 0],
    ['tunnel-street', 'line-opacity', 0],
    // borders: keep country lines visible, drop subdivisions
    ['admin-1-boundary', 'line-opacity', 0.18],
    ['admin-1-boundary-bg', 'line-opacity', 0],
    ['admin-0-boundary', 'line-color', '#ffffff'],
    ['admin-0-boundary', 'line-opacity', 0.35],
    ['admin-0-boundary-bg', 'line-opacity', 0.0],
    // labels: dark ink with a crisp white halo for legibility on the
    // light streets basemap (no halo blur — that was reading as a smear
    // back when the labels were white-on-dark for the satellite globe).
    ['country-label', 'text-color', '#1c1917'],
    ['country-label', 'text-halo-color', 'rgba(255,255,255,0.9)'],
    ['country-label', 'text-halo-width', 1.6],
    ['country-label', 'text-halo-blur', 0],
    ['settlement-major-label', 'text-color', '#1c1917'],
    ['settlement-major-label', 'text-halo-color', 'rgba(255,255,255,0.92)'],
    ['settlement-major-label', 'text-halo-width', 1.5],
    ['settlement-major-label', 'text-halo-blur', 0],
    ['settlement-minor-label', 'text-color', '#292524'],
    ['settlement-minor-label', 'text-halo-color', 'rgba(255,255,255,0.9)'],
    ['settlement-minor-label', 'text-halo-width', 1.3],
    ['settlement-minor-label', 'text-halo-blur', 0],
    ['settlement-subdivision-label', 'text-color', '#57534e'],
    ['settlement-subdivision-label', 'text-halo-color', 'rgba(255,255,255,0.85)'],
    ['settlement-subdivision-label', 'text-opacity', 0.7],
    // water labels: low key
    ['waterway-label', 'text-opacity', 0.5],
    ['water-point-label', 'text-opacity', 0.5],
  ];
  for (const [layer, prop, val] of tweaks) {
    // Guard with getLayer so absent layers don't trigger Mapbox's internal
    // error log (the try/catch swallows the throw but Mapbox still console.errors).
    if (!map.getLayer(layer)) continue;
    try { (map.setPaintProperty as (l: string, p: string, v: unknown) => void)(layer, prop, val); }
    catch { /* layer absent or property invalid */ }
  }
  // Atmosphere — Earth-from-orbit. Three-tone gradient with a warm-sunlit
  // horizon ring, soft Earth-blue upper atmosphere, deep cosmic space, and a
  // generous starfield. The light-theme cartography sits inside this dome,
  // glass chrome floats above it.
  try {
    map.setFog({
      // distance fall-off — fog is thin near camera, denser at horizon
      range: [0.5, 10],
      // horizon glow ring (warm, sunlit-edge)
      color: '#ffe7b5',
      // upper atmosphere visible just above the limb (Earth-blue)
      'high-color': '#5b8fc7',
      // deep space — slight blue-violet tint, not pure black
      'space-color': '#070612',
      'horizon-blend': 0.07,
      'star-intensity': 0.85,
    });
  } catch { /* projection may not support fog */ }
}

function installLayers(map: MapboxMap) {
  if (map.getSource(SOURCE)) return;
  map.addSource(SOURCE, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterMaxZoom: 4,
    clusterRadius: 32,
  });
  map.addSource(SOURCE_FLAT, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: false,
  });
  // Mapbox-hosted country polygons keyed by iso_3166_1 (ISO-2). Free tier;
  // every Mapbox style ships access to it. Used by the choropleth view.
  if (!map.getSource(SOURCE_COUNTRIES)) {
    map.addSource(SOURCE_COUNTRIES, {
      type: 'vector',
      url: 'mapbox://mapbox.country-boundaries-v1',
    });
  }

  // ----- country choropleth (rendered ABOVE the basemap but BELOW markers
  //       so individual outbreak pins still read through) -----
  map.addLayer({
    id: LAYER_CHOROPLETH,
    type: 'fill',
    source: SOURCE_COUNTRIES,
    'source-layer': 'country_boundaries',
    layout: { visibility: 'none' },
    paint: {
      // The `match` expression starts off as a transparent default; it gets
      // populated the moment outbreak data is pushed via setChoropleth().
      'fill-color': 'rgba(156, 163, 175, 0.0)',
      'fill-antialias': true,
    },
  });
  map.addLayer({
    id: LAYER_CHOROPLETH_OUTLINE,
    type: 'line',
    source: SOURCE_COUNTRIES,
    'source-layer': 'country_boundaries',
    layout: { visibility: 'none' },
    paint: {
      // Faint outline for countries that have any case count. Set via
      // setChoropleth() so it stays in sync with the fill expression.
      'line-color': 'rgba(82, 71, 229, 0.0)',
      'line-width': 0.8,
    },
  });

  // ----- heatmap (rendered first, hidden by default) -----
  map.addLayer({
    id: LAYER_HEATMAP,
    type: 'heatmap',
    source: SOURCE_FLAT,
    layout: { visibility: 'none' },
    paint: {
      // Per-point weight from precomputed log10(cases). Floor at 0.5 so
      // small outbreaks still register; a 3000-case epidemic doesn't drown
      // out a 5-case cluster — log scaling already compresses that range.
      'heatmap-weight': [
        'interpolate', ['linear'], ['get', 'weight'],
        0, 0.5,
        4, 1,
      ],
      // Intensity stays meaningful at every zoom — slight taper so dense
      // hotspots at globe view spread, while individual events at city view
      // still show as a clear bloom.
      'heatmap-intensity': [
        'interpolate', ['linear'], ['zoom'],
        0, 3,
        4, 2.5,
        8, 2.2,
        12, 2,
        16, 1.8,
      ],
      // Indigo → magenta → orange → signal red, with low-density visible
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(0,0,0,0)',
        0.05, 'rgba(82,71,229,0.55)',
        0.20, 'rgba(120,71,229,0.7)',
        0.40, 'rgba(200,80,200,0.8)',
        0.60, 'rgba(255,140,60,0.9)',
        0.80, 'rgba(255,80,60,0.95)',
        1, 'rgba(220,38,38,1)',
      ],
      // Radius scales with zoom so a single point stays a recognisable
      // bloom at every level — never collapses to a pixel. Kept tighter
      // than the default mapbox heatmap so clusters don't bleed across
      // continents at low zoom.
      'heatmap-radius': [
        'interpolate', ['linear'], ['zoom'],
        0, 32,
        4, 30,
        8, 30,
        12, 36,
        16, 48,
      ],
      // Mostly-opaque across zooms; gentle taper at extreme zoom so the
      // satellite imagery underneath stays readable
      'heatmap-opacity': [
        'interpolate', ['linear'], ['zoom'],
        0, 0.92,
        12, 0.82,
        16, 0.7,
      ],
    },
  });

  // ----- clusters: glow + glass disc + indigo ring + count -----
  map.addLayer({
    id: LAYER_CLUSTER_GLOW,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // soft outer glow — picks up the indigo accent
      'circle-color': '#5247e5',
      'circle-radius': ['step', ['get', 'point_count'], 22, 5, 26, 12, 32],
      'circle-opacity': 0.15,
      'circle-blur': 0.9,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_BG,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // glass disc — warm cream, very high opacity for legibility on dark space
      'circle-color': '#ffffff',
      'circle-radius': ['step', ['get', 'point_count'], 16, 5, 20, 12, 24],
      'circle-opacity': 0.97,
      'circle-stroke-color': 'rgba(82, 71, 229, 0.55)',
      'circle-stroke-width': 1.2,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_RING,
    type: 'circle',
    source: SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      // inner highlight ring — a hairline of accent inside the disc edge
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['step', ['get', 'point_count'], 13, 5, 17, 12, 21],
      'circle-stroke-color': '#5247e5',
      'circle-stroke-width': 0.75,
      'circle-stroke-opacity': 0.18,
    },
  });
  map.addLayer({
    id: LAYER_CLUSTER_COUNT,
    type: 'symbol',
    source: SOURCE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': 13,
      'text-allow-overlap': true,
      'text-offset': [0, 0.05],
    },
    paint: {
      'text-color': '#0c0a09',
    },
  });

  // ----- soft drop shadow under every point (cheap; visible on light theme) -----
  map.addLayer({
    id: LAYER_SHADOW,
    type: 'circle',
    source: SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': '#0c0a09',
      'circle-radius': ['+', ['get', 'radius'], 1],
      'circle-opacity': 0.10,
      'circle-blur': 1.2,
      'circle-translate': [0, 1],
    },
  });

  // ----- pulse: active outbreaks only — animated by component setInterval -----
  map.addLayer({
    id: LAYER_PULSE,
    type: 'circle',
    source: SOURCE,
    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'active'], 1]],
    paint: {
      'circle-color': '#dc2626',
      'circle-radius': ['*', ['get', 'radius'], 1.4],
      'circle-opacity': 0.4,
      'circle-blur': 0.5,
    },
  });

  // ----- selected: indigo ring around the selected feature -----
  map.addLayer({
    id: LAYER_SELECTED,
    type: 'circle',
    source: SOURCE,
    filter: ['==', ['get', 'id'], '__none__'],
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['+', ['get', 'radius'], 8],
      'circle-stroke-color': '#5247e5',
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 0.9,
    },
  });

  // ----- base point: white-ringed colored dot -----
  map.addLayer({
    id: LAYER_BASE,
    type: 'circle',
    source: SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': ['get', 'radius'],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.75,
      'circle-opacity': 1,
      'circle-radius-transition': { duration: 200 },
    },
  });
}

function applyViewMode(map: MapboxMap, mode: 'markers' | 'heatmap' | 'choropleth') {
  const heatmapVis = mode === 'heatmap' ? 'visible' : 'none';
  const choroplethVis = mode === 'choropleth' ? 'visible' : 'none';
  // Markers always show in marker mode; in choropleth mode we keep them
  // visible so users can still click into individual events on top of the
  // country fill. Heatmap mode hides markers (they'd clash with the
  // bloom).
  const markersVis = mode === 'heatmap' ? 'none' : 'visible';
  try { map.setLayoutProperty(LAYER_HEATMAP, 'visibility', heatmapVis); } catch { /* */ }
  for (const id of CHOROPLETH_LAYERS) {
    try { map.setLayoutProperty(id, 'visibility', choroplethVis); } catch { /* */ }
  }
  for (const id of MARKER_LAYERS) {
    try { map.setLayoutProperty(id, 'visibility', markersVis); } catch { /* */ }
  }
}

function pushData(map: MapboxMap, rows: OutbreakWithSources[]) {
  const fc = toFeatureCollection(rows);
  const clustered = map.getSource(SOURCE) as GeoJSONSource | undefined;
  if (clustered) clustered.setData(fc);
  const flat = map.getSource(SOURCE_FLAT) as GeoJSONSource | undefined;
  if (flat) flat.setData(fc);
  setChoropleth(map, rows);
}

function setChoropleth(map: MapboxMap, rows: OutbreakWithSources[]) {
  const byCountry = casesByCountry(rows);
  const fillExpr = buildChoroplethColorExpression(byCountry);
  // Build an outline expression that paints countries WITH cases in
  // indigo and leaves the rest transparent.
  const outlineExpr: unknown[] = ['match', ['get', 'iso_3166_1']];
  for (const code of byCountry.keys()) outlineExpr.push(code, 'rgba(82, 71, 229, 0.55)');
  outlineExpr.push('rgba(82, 71, 229, 0.0)');
  try {
    map.setPaintProperty(LAYER_CHOROPLETH, 'fill-color', fillExpr);
    map.setPaintProperty(
      LAYER_CHOROPLETH_OUTLINE,
      'line-color',
      outlineExpr as unknown as mapboxgl.ExpressionSpecification,
    );
  } catch { /* layers not installed yet — pushData will retry on next render */ }
}

function wireInteractions(map: MapboxMap, onSelectRef: React.MutableRefObject<(id: string | null) => void>) {
  for (const layer of [LAYER_BASE, LAYER_CLUSTER_BG]) {
    map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
  }

  map.on('click', LAYER_BASE, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const id = (f.properties as { id?: string } | null)?.id;
    if (id) onSelectRef.current(id);
  });

  map.on('click', LAYER_CLUSTER_BG, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const clusterId = (f.properties as { cluster_id?: number } | null)?.cluster_id;
    const src = map.getSource(SOURCE) as GeoJSONSource;
    if (clusterId === undefined) return;
    src.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null) return;
      map.easeTo({
        center: (f.geometry as GeoJSON.Point).coordinates as [number, number],
        zoom: zoom + 0.4,
        duration: 600,
      });
    });
  });

  map.on('click', (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: [LAYER_BASE, LAYER_CLUSTER_BG],
    });
    if (!features.length) onSelectRef.current(null);
  });
}
